import Drawer from "@components/drawer/drawer";
import Component from "@base/js/component";
import Calendar from "../calendar/calendar";

export default class MultiSelect extends Component {
  // Defines the element selector which will initialize this component
  static selector = ".multi-select";

  // Defines children that needs to be queried as part of this component
  static children = {
    checkboxes: ['input[type="checkbox"],input[type="radio"]'],
    checkboxContainers: [".checkbox"],
    heading: ".multi-select-heading",
    selections: ".multi-select__selections",
    search: ".multi-select__selections-search > input",
    searchClose: ".multi-select__selections-search__close",
    showMore: ".facets-soft-limit-link.show-more",
    showLess: ".facets-soft-limit-link.show-less",
  };

  // Defines sub components that needs to be queried as part of this component
  static members = {
    drawer: [Drawer],
    calendar: Calendar,
  };

  // Will save options filter group name. e.g. categories
  filterName = "";

  // Will save filter label
  filterLabel = "";

  // Will store queried elements and data
  options = {
    // optionValue: { element, label }
  };

  // Defines event handlers to attach to children
  eventHandlers = {
    checkboxes: {
      change: this.onCheckboxChange,
    },
  };

  // Will be called upon component instantiation
  afterInit() {
    const { search, searchClose, showMore, showLess } = this.children;

    this.filterName = this.element.dataset.filterName;
    this.filterLabel = this.children.heading.textContent;

    this.handleOptions();
    this.handleCalendar();

    if (showMore) showMore.addEventListener("click", this.showMore);
    if (showLess) showLess.addEventListener("click", this.showLess);
    if (search) search.addEventListener("input", this.handleSearch);
    if (searchClose)
      searchClose.addEventListener("click", this.onFilterSearchClose);
  }

  handleOptions() {
    this.children.checkboxes.forEach((checkbox) => {
      this.options[checkbox.value] = {
        element: checkbox,
        label: checkbox.parentElement.nextElementSibling.textContent,
        value: checkbox.value,
      };
    });
  }

  handleCalendar() {
    if (this.element.classList.contains(`multi-select--calendar`)) {
      this.members.calendar.children.submitCal.addEventListener(
        "click",
        (e) => {
          e.preventDefault();

          const selectedDateRange = this.element.querySelector(
            `[name="date_range"]:checked`
          );
          if (selectedDateRange.value === "date-specific-date") {
            this.emitEvent(selectedDateRange);
          }
        }
      );
    }
  }

  handleSearch = (e) => {
    const textQuery = e.target.value.toLowerCase();
    const filteredItems = this.children.checkboxContainers.filter(
      (checkboxContainer) => {
        const itemLabel = checkboxContainer.children[1].innerText.toLowerCase();

        return itemLabel.search(textQuery) >= 0; // if not negative, this is truthy
      }
    );

    if (filteredItems.length >= 1) {
      this.children.selections.innerHTML = "";

      filteredItems.forEach((item, index) => {
        if (index === 0) {
          item.setAttribute("tabindex", "0");
        } else {
          item.setAttribute("tabindex", "-1");
        }
        this.children.selections.appendChild(item);
      });
    } else {
      this.children.selections.innerHTML = "<span>No Results</span>";
    }

    this.switchSearchFilterIcons(textQuery);
  };

  switchSearchFilterIcons(isSearchOn) {
    const searchIcon = this.element.querySelector(".icon--search");
    const closeIcon = this.element.querySelector(
      ".multi-select__selections-search__close"
    );

    if (isSearchOn) {
      searchIcon.style.display = "none";
      closeIcon.style.display = "block";
    } else {
      searchIcon.style.display = "block";
      closeIcon.style.display = "none";
    }
  }

  onFilterSearchClose = () => {
    // clear search
    this.children.search.value = "";
    // remove no results text.
    this.children.selections.innerHTML = "";

    // reset items
    this.children.checkboxContainers.forEach((item, index) => {
      if (index === 0) {
        item.setAttribute("tabindex", "0");
      } else {
        item.setAttribute("tabindex", "-1");
      }
      this.children.selections.appendChild(item);
    });

    this.switchSearchFilterIcons(false);
  };

  onCheckboxChange(e) {
    const { currentTarget } = e;
    const options = Object.values(this.options);

    // If all is checked
    if (currentTarget.value === "All" && currentTarget.checked) {
      // Uncheck all others
      options.forEach((option) => {
        if (option.value !== "All") {
          option.element.checked = false;
        }
      });
    }
    // Else, uncheck "All"
    else if (this.options.All && this.options.All.element) {
      this.options.All.element.checked = false;
    }

    if (this.element.classList.contains(`multi-select--calendar`)) {
      this.element.querySelector(`.calendar`).style.display = "none";
      if (currentTarget.value === "date-specific-date") {
        this.element.querySelector(`.calendar`).style.display = "block";
        // false - means the Chip remove button (X) is clicked, so it should reload the page
        // true - the left radio button is the one selected, should display the calendar first
        if (currentTarget.checked === true) {
          return;
        }
      }
    }

    this.emitEvent(currentTarget);
  }

  emitEvent(e) {
    // Emit event
    const currentTarget = e;
    const currentOption = this.options[currentTarget.value];
    const customEvent = new window.CustomEvent("multiselectchange", {
      detail: {
        ...currentOption,
        isChecked: currentTarget.checked,
        filterName: this.filterName,
      },
    });

    this.element.dispatchEvent(customEvent);
  }

  // Checks an option without emitting event
  softSelect(value) {
    if (value in this.options) {
      this.options[value].element.checked = true;
    }
  }

  unselect(value) {
    if (value in this.options) {
      const customEvent = new window.CustomEvent("change");
      this.options[value].element.checked = false;
      this.options[value].element.dispatchEvent(customEvent);
    }
  }

  onChange(callback) {
    this.element.addEventListener("multiselectchange", (e) => callback(e));
  }

  clear() {
    Object.values(this.options).forEach((option) => {
      option.element.checked = false;
    });
  }

  showMore = () => {
    const { showMore, showLess, checkboxContainers } = this.children;

    if (checkboxContainers && checkboxContainers.length) {
      checkboxContainers.forEach((container) =>
        container.classList.remove("hidden")
      );
    }

    showLess.classList.remove("hidden");
    showMore.classList.add("hidden");
  };

  showLess = () => {
    const { showMore, showLess, checkboxContainers } = this.children;
    const { dataset } = showLess;

    const trimmedCheckboxes = checkboxContainers.slice(
      dataset.limit,
      checkboxContainers.length
    );

    trimmedCheckboxes.forEach((container) => container.classList.add("hidden"));

    showLess.classList.add("hidden");
    showMore.classList.remove("hidden");
  };
}

// Remove this line if this component is not intended
// to initialize on DOM load.
MultiSelect.init();
